// Alpaca Market Data adapter — BYO provider for US stocks/ETFs. Free
// API keys with any Alpaca account (free IEX feed; their paid plan
// upgrades to full-market SIP). Data-domain only: we never touch the
// trading API, so the keys can't place orders through us.
// Docs: https://docs.alpaca.markets/docs/about-market-data-api

import { storageGet, storageSet, storageRemove } from "../storage.js";
import { MarketDataError, type OHLCPoint } from "../market-data.js";
import type { DataProvider, CandleResult, ClosesResult } from "./types.js";

const CREDS_KEY = "providerAlpacaCreds";

/** Our interval vocabulary → Alpaca timeframe. Pure. */
export function alpacaTimeframe(interval: string): string | null {
  const map: Record<string, string> = {
    "1m": "1Min",
    "5m": "5Min",
    "15m": "15Min",
    "30m": "30Min",
    "60m": "1Hour",
    "1h": "1Hour",
    "1d": "1Day",
    "1wk": "1Week",
  };
  return map[interval] ?? null;
}

/** Plain US equity/ETF tickers only. Pure. */
export function alpacaSymbol(symbol: string): string | null {
  const s = symbol.trim().toUpperCase();
  return /^[A-Z.]{1,6}$/.test(s) ? s : null;
}

interface AlpacaBar {
  t: string;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

async function getCreds(): Promise<Record<string, string> | null> {
  return await storageGet<Record<string, string> | null>(CREDS_KEY, null);
}

async function fetchBars(symbol: string, range: string, interval: string): Promise<CandleResult> {
  const creds = await getCreds();
  if (!creds?.["keyId"] || !creds["secret"]) throw new MarketDataError("Alpaca not connected");
  const mapped = alpacaSymbol(symbol);
  const timeframe = alpacaTimeframe(interval);
  if (!mapped || !timeframe) throw new MarketDataError(`Alpaca can't serve ${symbol} @ ${interval}`);

  const { rangeToMs } = await import("./types.js");
  const start = new Date(Date.now() - rangeToMs(range)).toISOString();
  const url =
    `https://data.alpaca.markets/v2/stocks/${encodeURIComponent(mapped)}/bars` +
    `?timeframe=${timeframe}&start=${encodeURIComponent(start)}&limit=5000&adjustment=raw&feed=iex&sort=asc`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(url, {
      headers: { "APCA-API-KEY-ID": creds["keyId"], "APCA-API-SECRET-KEY": creds["secret"] },
      signal: controller.signal,
    });
    if (response.status === 401 || response.status === 403) throw new MarketDataError("Alpaca rejected the keys — check them in Settings → Data.");
    if (!response.ok) throw new MarketDataError(`Alpaca bars failed: ${response.status}`);
    const body = (await response.json()) as { bars?: AlpacaBar[] };
    const candles: OHLCPoint[] = (body.bars ?? []).map((b) => ({
      date: new Date(b.t),
      open: b.o,
      high: b.h,
      low: b.l,
      close: b.c,
      volume: b.v ?? 0,
    }));
    const last = candles[candles.length - 1];
    return { candles, currentPrice: last?.close };
  } finally {
    clearTimeout(timeout);
  }
}

export const alpacaProvider: DataProvider = {
  id: "alpaca",
  displayName: "Alpaca (your account)",
  assetClasses: ["Stocks"],
  realtime: true,
  setup: {
    signupUrl: "https://alpaca.markets/",
    keyLabel: "API Key ID + Secret",
    costNote: "Free with an Alpaca account (IEX feed); their paid plan upgrades coverage",
  },
  byo: {
    fields: [
      { key: "keyId", label: "API Key ID", type: "text" },
      { key: "secret", label: "API Secret", type: "password" },
    ],
    getCreds,
    async setCreds(creds) {
      if (creds) await storageSet(CREDS_KEY, creds);
      else await storageRemove(CREDS_KEY);
    },
    async verify(creds) {
      try {
        const response = await fetch("https://data.alpaca.markets/v2/stocks/AAPL/bars?timeframe=1Day&limit=1", {
          headers: { "APCA-API-KEY-ID": creds["keyId"] ?? "", "APCA-API-SECRET-KEY": creds["secret"] ?? "" },
        });
        if (response.ok) return { ok: true };
        return { ok: false, message: response.status === 401 || response.status === 403 ? "Alpaca rejected those keys." : `Alpaca error ${response.status}.` };
      } catch {
        return { ok: false, message: "Could not reach Alpaca." };
      }
    },
  },
  candles: fetchBars,
  async closes(symbol, range, interval): Promise<ClosesResult> {
    const { candles, currentPrice } = await fetchBars(symbol, range, interval);
    return { closes: candles.map((c) => c.close), regularMarketPrice: currentPrice };
  },
};
